import { ID, IDPrefixes } from '@griply/types';
import { defaultTagColorOption } from '@griply/utils';
import { useCreateTag, useSortedTags } from 'features/tags';
import { useUpdateTaskRelationships } from 'features/tasks';
import React from 'react';
import { TaskMetaInteractionTags as TaskMetaInteraction } from 'shared/components/ui/task-meta-interaction-tags';
import { useOpenCreateTagDialog } from 'shared/contexts/tag-form';
import { useRelationshipIdsById } from 'shared/hooks/use-relationships';
import { useTheme } from 'styled-components';

import * as Styled from './task-meta-interactions.style';

export type TaskMetaInteractionTagsProps = {
  id: ID;
  tagIds?: ID[];
  isMobileOutlined?: boolean;
};

export const TaskMetaInteractionTags: React.FC<
  TaskMetaInteractionTagsProps
> = ({ id, tagIds: explicitTagIds, isMobileOutlined }) => {
  const theme = useTheme();
  const tags = useSortedTags();
  const { tag: storedTagIds } = useRelationshipIdsById(id, IDPrefixes.Task);
  const tagIds = explicitTagIds ?? storedTagIds;

  const updateRelationship = useUpdateTaskRelationships(id, IDPrefixes.Tag);
  const { submit: createTag } = useCreateTag();
  const openCreateTagDialog = useOpenCreateTagDialog();

  const onCreate = async (name: string) => {
    if (!name) {
      openCreateTagDialog({ navigateOnCreate: false });
      return;
    }

    const newTag = await createTag({
      name,
      color: defaultTagColorOption.value,
    });
    updateRelationship([...tagIds, newTag.id]);
  };

  if (!tagIds.length) {
    return null;
  }

  return theme.isMobile ? null : (
    <Styled.ListItem
      $hasValue={!!tagIds.length}
      $isMobileOutlined={!!isMobileOutlined}
    >
      <TaskMetaInteraction
        tags={tags}
        value={tagIds}
        onChange={updateRelationship}
        onCreate={onCreate}
        redirectToTag
      />
    </Styled.ListItem>
  );
};
